import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  FileJson,
  ChevronLeft,
  ChevronRight,
  Upload,
  Cpu,
  Eye,
  Trash2,
  RefreshCw,
  X,
  File,
  CheckCircle,
  AlertCircle,
  Clock,
  Zap,
} from "lucide-react";
import "./GFSV2WorkOrders.css";

const MODEL_CONFIG = {
  name: "GFS V2",
  description: "Tesseract + Qwen VL",
  color: "#8b5cf6",
  bgColor: "#f5f3ff",
};

const FORM_TYPES = [
  { value: "aof", label: "Account Opening Form (AOF)" },
  { value: "redemption", label: "Redemption (RP)" },
  { value: "additional_purchase", label: "Additional Purchase (AP)" },
];

// Custom Dropdown Component for Status Filter
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="gfs-v2-custom-dropdown" ref={dropdownRef}>
      <button className="gfs-v2-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="gfs-v2-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`gfs-v2-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component - V2 has Auto Form Detection
const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') || 
      f.name.toLowerCase().endsWith('.tiff') ||
      f.name.toLowerCase().endsWith('.tif') ||
      f.name.toLowerCase().endsWith('.png') ||
      f.name.toLowerCase().endsWith('.jpg') ||
      f.name.toLowerCase().endsWith('.jpeg')
    );
    const existingNames = files.map(f => f.name);
    const uniqueFiles = validFiles.filter(f => !existingNames.includes(f.name));
    
    if (validFiles.length !== newFiles.length) {
      alert('Only PDF, TIFF, PNG, JPG files are supported.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    const newStatus = {};
    uniqueFiles.forEach(f => { newStatus[f.name] = 'pending'; });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];
    const token = localStorage.getItem("token");

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const formData = new FormData();
        formData.append("file", file);
        // V2 does NOT require form_type - auto-detects

        const res = await fetch("/v1/gfs/api/v2/extract", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const data = await res.json();
        if (res.ok) {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
          results.push({ 
            filename: file.name, 
            success: true, 
            work_order_id: data.work_order?.id,
            detected_form_type: data.meta_data?.form_type
          });
        } else {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
          results.push({ filename: file.name, success: false, error: data.message || "Upload failed" });
        }
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    if (onUploadComplete) onUploadComplete(results);
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading': return <RefreshCw size={16} className="spin" style={{ color: '#8b5cf6' }} />;
      case 'success': return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error': return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default: return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="gfs-v2-modal-overlay" onClick={handleClose}>
      <div className="gfs-v2-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="gfs-v2-modal-header" style={{ borderBottomColor: MODEL_CONFIG.color }}>
          <h3>
            <Upload size={20} style={{ color: MODEL_CONFIG.color }} />
            Upload Files - {MODEL_CONFIG.name}
          </h3>
          <button className="gfs-v2-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="gfs-v2-modal-body">
          {/* V2 Auto Detection Info */}
          <div className="gfs-v2-auto-detect-info">
            <Zap size={18} />
            <div>
              <strong>Auto Form Detection</strong>
              <p>V2 automatically detects form type using Tesseract OCR - no manual selection required!</p>
            </div>
          </div>

          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            className="gfs-v2-drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} style={{ color: MODEL_CONFIG.color }} />
            <p>Drag & drop files here</p>
            <span>or click to browse (PDF, TIFF, PNG, JPG)</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.tiff,.tif,.png,.jpg,.jpeg"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="gfs-v2-file-list">
              <div className="gfs-v2-file-list-header">
                <span>{files.length} file(s) selected</span>
                {allUploaded && (
                  <span className="gfs-v2-upload-summary">
                    {successCount} success, {errorCount} failed
                  </span>
                )}
              </div>
              <div className="gfs-v2-file-items">
                {files.map(file => {
                  const result = uploadResults.find(r => r.filename === file.name);
                  return (
                    <div key={file.name} className={`gfs-v2-file-item ${uploadStatus[file.name]}`}>
                      <div className="gfs-v2-file-info">
                        <File size={16} />
                        <span className="gfs-v2-file-name" title={file.name}>
                          {file.name.length > 40 ? file.name.substring(0, 40) + '...' : file.name}
                        </span>
                        <span className="gfs-v2-file-size">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                      {uploadStatus[file.name] === 'success' && result?.detected_form_type && (
                        <div className="gfs-v2-detected-type">
                          Detected: <strong>{FORM_TYPES.find(f => f.value === result.detected_form_type)?.label || result.detected_form_type}</strong>
                        </div>
                      )}
                      {uploadStatus[file.name] === 'error' && result?.error && (
                        <div className="gfs-v2-file-error">
                          {result.error}
                        </div>
                      )}
                      <div className="gfs-v2-file-actions">
                        {getStatusIcon(uploadStatus[file.name])}
                        {uploadStatus[file.name] === 'pending' && !uploading && (
                          <button className="gfs-v2-file-remove" onClick={() => removeFile(file.name)}>
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="gfs-v2-modal-footer">
          <button className="gfs-v2-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="gfs-v2-btn-upload-all"
              style={{ backgroundColor: MODEL_CONFIG.color }}
              onClick={handleUploadAll}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <><RefreshCw size={16} className="spin" /> Processing...</>
              ) : (
                <><Upload size={16} /> Upload {files.length} File{files.length !== 1 ? 's' : ''}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const GFSV2WorkOrders = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10, skip: 0 });
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async (skip = 0) => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const statusParam = statusFilter !== "all" ? `&status=${statusFilter}` : "";
      const [ordersRes, statsRes] = await Promise.all([
        fetch(`/v1/gfs/api/work-orders?skip=${skip}&limit=10&version=v2${statusParam}`, { headers }).then(r => r.json()),
        fetch("/v1/gfs/api/stats?version=v2", { headers }).then(r => r.json()),
      ]);

      if (ordersRes.work_orders) {
        setWorkOrders(ordersRes.work_orders);
        setPagination(ordersRes.pagination || { total: ordersRes.work_orders.length, page: 1, pages: 1, limit: 10, skip: 0 });
      }

      if (statsRes) {
        setStats({
          total: statsRes.total_work_orders || 0,
          completed: statsRes.by_status?.completed || 0,
          processing: statsRes.by_status?.processing || 0,
          pending: statsRes.by_status?.pending || 0,
          failed: statsRes.by_status?.failed || 0,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = (results) => {
    fetchData(0);
    const successResults = results.filter(r => r.success);
    if (successResults.length === 1 && successResults[0].work_order_id) {
      setShowUploadModal(false);
      navigate(`/gfs/v2/work-order/${successResults[0].work_order_id}`);
    }
  };

  const handleDelete = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this work order?")) return;
    try {
      const token = localStorage.getItem("token");
      await fetch(`/v1/gfs/api/work-orders/${workOrderId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchData(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchData(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  const filteredWorkOrders = workOrders.filter((wo) => {
    const term = searchTerm.toLowerCase();
    return (
      wo._id?.toLowerCase().includes(term) ||
      wo.filename?.toLowerCase().includes(term) ||
      wo.uid?.toLowerCase().includes(term)
    );
  });

  const getStatusBadgeClass = (status) => {
    const classes = { completed: "status-completed", processing: "status-processing", pending: "status-pending", failed: "status-failed" };
    return classes[status] || "status-pending";
  };

  const getFormTypeLabel = (type) => {
    return FORM_TYPES.find(f => f.value === type)?.label || type;
  };

  return (
    <div className="gfs-v2-work-orders-list">
      {/* Header with Model Info */}
      <div className="gfs-v2-header-banner" style={{ borderLeftColor: MODEL_CONFIG.color }}>
        <div className="gfs-v2-header-info">
          <h2 style={{ color: MODEL_CONFIG.color }}>
            <Cpu size={24} />
            {MODEL_CONFIG.name} Work Orders
          </h2>
          <p>{MODEL_CONFIG.description} - Auto Form Detection Pipeline</p>
        </div>
        <div className="gfs-v2-header-actions">
          <button
            className="gfs-v2-btn-upload"
            onClick={() => setShowUploadModal(true)}
            style={{ backgroundColor: MODEL_CONFIG.color }}
          >
            <Upload size={18} /> Upload & Extract
          </button>
        </div>
      </div>

      {error && (
        <div className="gfs-v2-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="gfs-v2-stats-grid">
        <div className="gfs-v2-stat-card">
          <div className="gfs-v2-stat-value">{stats.total}</div>
          <div className="gfs-v2-stat-label">Total</div>
        </div>
        <div className="gfs-v2-stat-card completed">
          <div className="gfs-v2-stat-value">{stats.completed}</div>
          <div className="gfs-v2-stat-label">Completed</div>
        </div>
        <div className="gfs-v2-stat-card processing">
          <div className="gfs-v2-stat-value">{stats.processing}</div>
          <div className="gfs-v2-stat-label">Processing</div>
        </div>
        <div className="gfs-v2-stat-card pending">
          <div className="gfs-v2-stat-value">{stats.pending}</div>
          <div className="gfs-v2-stat-label">Pending</div>
        </div>
        <div className="gfs-v2-stat-card failed">
          <div className="gfs-v2-stat-value">{stats.failed}</div>
          <div className="gfs-v2-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="gfs-v2-filters-row">
        <input
          type="text"
          placeholder="Search by ID, filename, or UID..."
          className="gfs-v2-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <CustomStatusDropdown value={statusFilter} onChange={setStatusFilter} />
        <button className="gfs-v2-btn-refresh" onClick={() => fetchData(0)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="gfs-v2-table-container">
        <table className="gfs-v2-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Filename</th>
              <th>Detected Form</th>
              <th>Status</th>
              <th>Pages</th>
              <th>Created</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="gfs-v2-table-loading">
                  <RefreshCw size={20} className="spin" /> Loading...
                </td>
              </tr>
            ) : filteredWorkOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="gfs-v2-table-empty">
                  No work orders found. Upload a document to get started.
                </td>
              </tr>
            ) : (
              filteredWorkOrders.map((wo) => (
                <tr key={wo._id} onClick={() => navigate(`/gfs/v2/work-order/${wo._id}`)}>
                  <td className="gfs-v2-id-cell">{wo._id?.slice(-8)}</td>
                  <td className="gfs-v2-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 30) || "-"}
                    {wo.filename?.length > 30 && "..."}
                  </td>
                  <td>
                    <span className="gfs-v2-form-badge" style={{ backgroundColor: MODEL_CONFIG.bgColor, color: MODEL_CONFIG.color }}>
                      {getFormTypeLabel(wo.form_type)}
                      {wo.form_type_auto_detected && <span className="auto-tag">Auto</span>}
                    </span>
                  </td>
                  <td>
                    <span className={`gfs-v2-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td>{wo.total_pages || "-"}</td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td>{wo.processing_time ? `${wo.processing_time.toFixed(1)}s` : "-"}</td>
                  <td className="gfs-v2-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button className="gfs-v2-action-btn view" title="View Details" onClick={() => navigate(`/gfs/v2/work-order/${wo._id}`)}>
                      <Eye size={16} />
                    </button>
                    <button
                      className="gfs-v2-action-btn json"
                      title="Download JSON"
                      onClick={(e) => {
                        e.stopPropagation();
                        const data = wo.extracted_data || {};
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${wo.filename?.replace(/\.[^/.]+$/, "") || wo._id}.json`;
                        a.click();
                      }}
                    >
                      <FileJson size={16} />
                    </button>
                    <button className="gfs-v2-action-btn delete" title="Delete" onClick={(e) => handleDelete(wo._id, e)}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="gfs-v2-pagination">
        <div className="gfs-v2-pagination-info">
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </div>
        <div className="gfs-v2-pagination-controls">
          <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="gfs-v2-page-indicator">
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

export default GFSV2WorkOrders;
